import { useRouter } from "next/navigation";
import { useRef, useEffect, useState, useCallback } from "react";

const clearAiChatLocalStorage = (): void => {
  sessionStorage.removeItem("roomData");
  sessionStorage.removeItem("failedMessages");
  sessionStorage.removeItem("roomConversationStarted");
};

/**
 * Hook to automatically clear press release localStorage when navigating away from press release pages
 * Should be used in a component that's always mounted (e.g., Providers, Layout)
 */
export const useAiChatNavigation = (): {
  showLeaveDialog: boolean;
  setShowLeaveDialog: (show: boolean) => void;
  handleLeaveConfirm: () => void;
  handleCancelLeave: () => void;
} => {
  const router = useRouter();
  const pendingNavigationRef = useRef<string | null>(null);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);

  const handleLeaveConfirm = useCallback(() => {
    setShowLeaveDialog(false);

    // Navigate to the pending route if exists
    if (pendingNavigationRef.current) {
      const target = pendingNavigationRef.current;
      pendingNavigationRef.current = null;
      // Pricing opens Insight Pilot in a new tab; the conversation on the
      // current AI chat page stays untouched and its storage is kept
      if (
        target === "/pricing" ||
        target.startsWith("/pricing/") ||
        target.startsWith("/pricing?")
      ) {
        window.open("https://insight-pilot.ekbana.ai/", "_blank", "noopener,noreferrer");
        return;
      }
      clearAiChatLocalStorage();
      if (
        target.startsWith("http://") ||
        target.startsWith("https://")
      ) {
        window.location.href = target;
      } else {
        router.push(target);
      }
    }
  }, [router]);

  const handleCancelLeave = useCallback(() => {
    setShowLeaveDialog(false);
    pendingNavigationRef.current = null;
    // Stay on the current AI chat page (no navigation needed)
  }, []);

  useEffect(() => {
    // Listen for the custom event from safe navigation
    const handleShowDialog = (event: CustomEvent) => {
      const { targetRoute } = event.detail;
      pendingNavigationRef.current = targetRoute;
      setShowLeaveDialog(true);
    };

    globalThis.addEventListener(
      "showLeaveAiChatDialog",
      handleShowDialog as EventListener
    );

    return () => {
      globalThis.removeEventListener(
        "showLeaveAiChatDialog",
        handleShowDialog as EventListener
      );
    };
  }, []);

  return {
    showLeaveDialog,
    setShowLeaveDialog,
    handleLeaveConfirm,
    handleCancelLeave,
  };
};
